import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";

import styles from "./AnalyzeTopicOutput.module.css";
import {
  type ToonTable,
  detectAnalyzeTopicPayload,
} from "./analyzeTopicPayload";

interface TopicIdListProps {
  topicIds: string[];
}

function TopicIdList({ topicIds }: TopicIdListProps): React.JSX.Element {
  if (topicIds.length === 0) {
    return (
      <RobotoTypography component="p" className={styles.topicIdsEmpty}>
        No topics matched
      </RobotoTypography>
    );
  }
  return (
    <div className={styles.topicIds}>
      {topicIds.map((id) => (
        <span key={id} className={styles.topicIdChip}>
          {id}
        </span>
      ))}
    </div>
  );
}

function renderCell(value: string | boolean | null): React.ReactNode {
  if (value === null) {
    return <span className={styles.nullCell}>—</span>;
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return value;
}

interface ToonTableViewProps {
  table: ToonTable;
}

function ToonTableView({ table }: ToonTableViewProps): React.JSX.Element {
  if (table.columns.length === 0 && table.rows.length === 0) {
    return (
      <RobotoTypography component="p" className={styles.emptyTable}>
        Query returned no columns.
      </RobotoTypography>
    );
  }
  return (
    <div className={styles.tableScroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            {table.columns.map((col, idx) => (
              <th key={`${col}-${idx}`}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rIdx) => (
            <tr key={rIdx}>
              {table.columns.map((_, cIdx) => (
                <td key={cIdx}>{renderCell(row[cIdx] ?? null)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface AnalyzeTopicOutputProps {
  output: unknown;
}

/**
 * Renderer for the ``Output`` section of an analyze_topic tool result. Returns
 * ``null`` if the payload doesn't match any known shape — the caller then
 * falls back to the generic JSON view so nothing is hidden if the contract
 * ever shifts under us.
 */
export function AnalyzeTopicOutput({
  output,
}: AnalyzeTopicOutputProps): React.JSX.Element | null {
  const payload = React.useMemo(
    () => detectAnalyzeTopicPayload(output),
    [output],
  );

  if (payload.kind === "text") {
    return <pre className={styles.errorText}>{payload.text}</pre>;
  }

  if (payload.kind === "unknown") {
    return null;
  }

  const { topicIds, table } = payload;
  const totalMatchingRows =
    payload.kind === "toon" ? payload.totalMatchingRows : undefined;
  const note = payload.kind === "toon" ? payload.note : undefined;
  const dataLabel =
    totalMatchingRows !== undefined
      ? `data (first ${table.rowCount} of ${totalMatchingRows} matching rows)`
      : `data (${table.rowCount} ${table.rowCount === 1 ? "row" : "rows"})`;
  return (
    <div className={styles.container}>
      <div className={styles.topicIdsSection}>
        <RobotoTypography component="p" className={styles.subLabel}>
          topic_ids ({topicIds.length})
        </RobotoTypography>
        <TopicIdList topicIds={topicIds} />
      </div>
      <div className={styles.tableSection}>
        <RobotoTypography component="p" className={styles.subLabel}>
          {dataLabel}
        </RobotoTypography>
        {note !== undefined && (
          <RobotoTypography component="p" className={styles.note}>
            {note}
          </RobotoTypography>
        )}
        <ToonTableView table={table} />
      </div>
    </div>
  );
}
